<template>
  <section class="member-profile tw-page">
    <div class="tw-container">

      <div v-if="isLoading" class="tw-state">
        <b-loading :is-full-page="false" :active="true" />
        <p>Loading profile...</p>
      </div>

      <div v-else-if="isPrivate" class="tw-state member-profile__not-found">
        <i class="mdi mdi-lock-outline member-profile__not-found-icon"></i>
        <p class="member-profile__not-found-title">This profile is private.</p>
        <p class="member-profile__not-found-sub">The owner has not enabled public access for this profile.</p>
      </div>

      <div v-else-if="notFound" class="tw-state member-profile__not-found">
        <i class="mdi mdi-account-off-outline member-profile__not-found-icon"></i>
        <p class="member-profile__not-found-title">No profile set up for this member yet.</p>

        <template v-if="isAdminViewingOther || (isSelf && user?.isAdmin)">
          <p class="member-profile__not-found-sub">Initialize the profile by assigning a role below.</p>
          <div class="member-profile__init-form">
            <b-field label="Role">
              <b-select v-model="initProfile.role">
                <option value="individual">Individual</option>
                <option value="manager">Manager</option>
                <option value="executive">Executive</option>
              </b-select>
            </b-field>
            <b-field label="Display name (optional)">
              <b-input v-model="initProfile.displayName" placeholder="Full name shown on profile" />
            </b-field>
            <b-field label="Job title (optional)">
              <b-input v-model="initProfile.title" placeholder="e.g. Senior Engineer" />
            </b-field>
            <p v-if="initProfile.error" class="member-profile__init-error">{{ initProfile.error }}</p>
            <b-button
              type="is-primary"
              :loading="initProfile.saving"
              @click="initializeMemberProfile"
            >
              Initialize Profile
            </b-button>
          </div>
        </template>
        <p v-else class="member-profile__not-found-sub">Contact a company admin to set up this profile.</p>
      </div>

      <template v-else-if="profile">
        <div class="member-profile__header tw-card">
          <div class="member-profile__identity">
            <template v-if="isEditing">
              <b-field
                :type="editError ? 'is-danger' : ''"
                :message="editError"
              >
                <b-input
                  v-model="editForm.displayName"
                  placeholder="Display name"
                  size="is-medium"
                  @input="editError = ''"
                />
              </b-field>
              <b-field>
                <b-input
                  v-model="editForm.title"
                  placeholder="Title"
                />
              </b-field>
              <div class="member-profile__edit-actions">
                <b-button
                  type="is-primary"
                  size="is-small"
                  :loading="isSaving"
                  @click="saveProfile"
                >
                  Save
                </b-button>
                <b-button
                  type="is-text"
                  size="is-small"
                  :disabled="isSaving"
                  @click="cancelEdit"
                >
                  Cancel
                </b-button>
              </div>
            </template>

            <template v-else>
              <h1 class="member-profile__name">{{ profile.displayName || '—' }}</h1>
              <p v-if="profile.title" class="member-profile__title">{{ profile.title }}</p>
              <p v-if="companyName" class="member-profile__company">
                <i class="mdi mdi-domain"></i>
                {{ companyName }}
              </p>
            </template>
          </div>

          <div class="member-profile__meta">
            <span class="member-profile__role-badge" :class="`member-profile__role-badge--${profile.role}`">
              {{ roleLabel }}
            </span>
            <span
              v-if="isSelf"
              class="member-profile__public-badge"
              :class="publicSettings.publicEnabled ? 'member-profile__public-badge--on' : 'member-profile__public-badge--off'"
            >
              <i :class="publicSettings.publicEnabled ? 'mdi mdi-earth' : 'mdi mdi-lock-outline'"></i>
              {{ publicSettings.publicEnabled ? 'Public profile on' : 'Not public' }}
            </span>
            <span
              v-else
              class="member-profile__public-badge"
              :class="profile.publicEnabled ? 'member-profile__public-badge--on' : 'member-profile__public-badge--off'"
            >
              <i :class="profile.publicEnabled ? 'mdi mdi-earth' : 'mdi mdi-lock-outline'"></i>
              {{ profile.publicEnabled ? 'Public profile on' : 'Public profile off' }}
            </span>
            <b-button
              v-if="isSelf && !isEditing"
              type="is-light"
              size="is-small"
              icon-left="pencil"
              @click="startEdit"
            >
              Edit
            </b-button>
            <b-button
              v-if="isSelf"
              type="is-light"
              size="is-small"
              :icon-left="previewMode ? 'eye-off' : 'eye'"
              @click="previewMode = !previewMode"
            >
              {{ previewMode ? 'Exit preview' : 'Preview public' }}
            </b-button>
          </div>
        </div>

        <!-- Preview mode banner -->
        <div v-if="previewMode" class="member-profile__preview-banner">
          <i class="mdi mdi-eye"></i>
          <strong>Preview mode</strong> — you are viewing your profile as a public visitor would see it.
          <b-button
            v-if="publicSettings.publicEnabled"
            type="is-light"
            size="is-small"
            :icon-left="copyLinkSuccess ? 'check' : 'link-variant'"
            @click="copyPublicLink"
          >
            {{ copyLinkSuccess ? 'Copied!' : 'Copy public link' }}
          </b-button>
          <span v-else class="member-profile__preview-disabled">Public profile is off — enable it in settings below to share this URL.</span>
        </div>

        <!-- Professional Summary -->
        <div
          v-if="resumeData.summary && (!previewMode || publicSettings.visibleFields.includes('summary'))"
          class="member-profile__section tw-card"
        >
          <h2 class="member-profile__section-title">Professional Summary</h2>
          <p class="member-profile__summary-text">{{ resumeData.summary }}</p>
        </div>

        <div v-if="!previewMode && viewerRole !== 'public'" class="member-profile__section tw-card">
          <div class="member-profile__section-header">
            <h2 class="member-profile__section-title">Work Record</h2>
            <div class="member-profile__wr-range">
              <b-button
                v-for="d in [30, 60, 90]"
                :key="d"
                size="is-small"
                :type="wr.days === d ? 'is-primary' : 'is-light'"
                @click="selectDays(d)"
              >{{ d }}d</b-button>
            </div>
          </div>

          <div v-if="wr.loading" class="member-profile__wr-loading">
            <b-loading :is-full-page="false" :active="true" />
            <p>Loading work record…</p>
          </div>

          <p v-else-if="wr.error" class="member-profile__wr-error">{{ wr.error }}</p>

          <div v-else-if="!wr.data || wr.data.noMapping" class="member-profile__empty-state">
            <i class="mdi mdi-chart-timeline-variant member-profile__empty-icon"></i>
            <p class="member-profile__empty-title">PSA mapping required</p>
            <p class="member-profile__empty-sub">
              Once your account is connected to a PSA member, your work activity will appear here.
            </p>
          </div>

          <template v-else>
            <div v-if="wr.data.staleWarning" class="member-profile__wr-warning">
              <i class="mdi mdi-clock-alert-outline"></i>
              PSA data may be stale — last sync was over 48 hours ago.
            </div>
            <div v-if="wr.data.summary.minDataWarning" class="member-profile__wr-warning member-profile__wr-warning--info">
              <i class="mdi mdi-information-outline"></i>
              Fewer than 10 tickets — results may not be representative.
            </div>

            <div class="member-profile__wr-stats">
              <div class="member-profile__wr-stat">
                <span class="member-profile__wr-stat-value">{{ wr.data.summary.ticketCount }}</span>
                <span class="member-profile__wr-stat-label">Tickets</span>
              </div>
              <div class="member-profile__wr-stat">
                <span class="member-profile__wr-stat-value">{{ wr.data.summary.totalHours }}</span>
                <span class="member-profile__wr-stat-label">Total hours</span>
              </div>
              <div class="member-profile__wr-stat">
                <span class="member-profile__wr-stat-value">{{ wr.data.summary.billableHours }}</span>
                <span class="member-profile__wr-stat-label">Billable hours</span>
              </div>
            </div>

            <div v-if="wr.data.summary.categoryBreakdown.length" class="member-profile__wr-group">
              <h3 class="member-profile__wr-group-title">Work Mix</h3>
              <div class="member-profile__wr-rows">
                <div
                  v-for="item in wr.data.summary.categoryBreakdown"
                  :key="item.category"
                  class="member-profile__wr-row"
                >
                  <span class="member-profile__wr-row-label">{{ item.category }}</span>
                  <span class="member-profile__wr-row-count">{{ item.count }} ticket{{ item.count !== 1 ? 's' : '' }}</span>
                  <span class="member-profile__wr-row-hours">{{ item.hours }}h</span>
                </div>
              </div>
            </div>

            <div v-if="wr.data.summary.issueTypeDistribution.length" class="member-profile__wr-group">
              <h3 class="member-profile__wr-group-title">Issue Categories</h3>
              <div class="member-profile__wr-rows">
                <div
                  v-for="item in wr.data.summary.issueTypeDistribution"
                  :key="item.type"
                  class="member-profile__wr-row"
                >
                  <span class="member-profile__wr-row-label">{{ item.type }}</span>
                  <span class="member-profile__wr-row-count">{{ item.count }}</span>
                  <span class="member-profile__wr-row-pct">{{ item.percent }}%</span>
                </div>
              </div>
            </div>

            <div v-if="wr.data.clientExposure && wr.data.clientExposure.length" class="member-profile__wr-group">
              <h3 class="member-profile__wr-group-title">Client Exposure</h3>
              <div class="member-profile__wr-rows">
                <div
                  v-for="item in wr.data.clientExposure"
                  :key="item.clientName"
                  class="member-profile__wr-row"
                >
                  <span class="member-profile__wr-row-label">{{ item.clientName }}</span>
                  <span class="member-profile__wr-row-count">{{ item.ticketCount }} ticket{{ item.ticketCount !== 1 ? 's' : '' }}</span>
                  <span class="member-profile__wr-row-hours">{{ item.hours }}h</span>
                </div>
              </div>
            </div>

            <div v-if="wr.data.repeatPatterns.length" class="member-profile__wr-group">
              <h3 class="member-profile__wr-group-title">Repeat Patterns</h3>
              <div class="member-profile__wr-rows">
                <div
                  v-for="item in wr.data.repeatPatterns"
                  :key="item.pattern"
                  class="member-profile__wr-row"
                >
                  <span class="member-profile__wr-row-label">{{ item.pattern }}</span>
                  <span class="member-profile__wr-row-count">× {{ item.count }}</span>
                </div>
              </div>
            </div>

            <div v-if="wr.data.representativeExamples.length" class="member-profile__wr-group">
              <h3 class="member-profile__wr-group-title">Representative Examples</h3>
              <div class="member-profile__wr-examples">
                <div
                  v-for="ex in wr.data.representativeExamples"
                  :key="ex.ticketNbr"
                  class="member-profile__wr-example"
                >
                  <div class="member-profile__wr-example-meta">
                    <span class="member-profile__wr-example-nbr">{{ ex.ticketNbr }}</span>
                    <span v-if="ex.category" class="member-profile__wr-example-cat">{{ ex.category }}</span>
                    <span v-if="ex.type" class="member-profile__wr-example-type">{{ ex.type }}</span>
                  </div>
                  <div class="member-profile__wr-example-summary">{{ ex.summary || '(no summary)' }}</div>
                  <div class="member-profile__wr-example-hours">{{ ex.hours }}h</div>
                </div>
              </div>
            </div>

            <div class="member-profile__wr-group member-profile__wr-context">
              <h3 class="member-profile__wr-group-title snapshot-card__label">Data context and limits</h3>
              <dl class="dataset-context">
                <template v-for="item in wr.data.dataContext.items" :key="item.label">
                  <dt>{{ item.label }}</dt>
                  <dd>{{ item.value }}</dd>
                </template>
              </dl>
              <ul v-if="wr.data.dataContext.limitations.length" class="member-profile__wr-limitations">
                <li v-for="(lim, idx) in wr.data.dataContext.limitations" :key="idx">{{ lim }}</li>
              </ul>
            </div>
          </template>
        </div>

        <!-- Goals & Growth Direction -->
        <div v-if="(isSelf || (user && user.isAdmin)) && !previewMode" class="member-profile__section tw-card">
          <h2 class="member-profile__section-title">Goals & Growth Direction</h2>

          <div v-if="profile.goals && profile.goals.length" class="member-profile__goals">
            <div
              v-for="goal in profile.goals"
              :key="goal._id"
              class="member-profile__goal"
            >
              <template v-if="goals.editingId === goal._id">
                <b-field class="member-profile__goal-edit-field">
                  <b-input
                    v-model="goals.editText"
                    type="textarea"
                    rows="2"
                    @keyup.ctrl.enter="saveGoal(goal._id)"
                  />
                </b-field>
                <div class="member-profile__goal-actions">
                  <b-button size="is-small" type="is-primary" :loading="goals.saving" @click="saveGoal(goal._id)">Save</b-button>
                  <b-button size="is-small" type="is-text" :disabled="goals.saving" @click="cancelEditGoal">Cancel</b-button>
                </div>
              </template>
              <template v-else>
                <p class="member-profile__goal-text">{{ goal.text }}</p>
                <div v-if="isSelf" class="member-profile__goal-actions">
                  <b-button size="is-small" type="is-light" icon-left="pencil" @click="startEditGoal(goal)">Edit</b-button>
                  <b-button size="is-small" type="is-light" icon-left="delete" @click="removeGoal(goal._id)">Remove</b-button>
                </div>
              </template>
            </div>
          </div>
          <p v-else-if="isSelf" class="member-profile__empty-inline">No goals added yet. Add your first development goal below.</p>
          <p v-else class="member-profile__empty-inline">No goals listed.</p>

          <template v-if="isSelf">
            <div class="member-profile__add-row member-profile__add-row--block">
              <b-input
                v-model="goals.newText"
                type="textarea"
                placeholder="Describe a development goal or growth direction…"
                rows="2"
              />
              <b-button
                type="is-primary"
                size="is-small"
                :loading="goals.saving"
                :disabled="!goals.newText.trim()"
                @click="addGoal"
              >Add Goal</b-button>
            </div>
            <p v-if="goals.error" class="member-profile__inline-error">{{ goals.error }}</p>
          </template>
        </div>

        <!-- Work-to-Growth Alignment -->
        <div v-if="canViewAlignment && !previewMode" class="member-profile__section tw-card">
          <h2 class="member-profile__section-title">Work-to-Growth Alignment</h2>

          <div v-if="alignment.loading" class="member-profile__alignment-loading">
            <b-loading :is-full-page="false" :active="true" />
            <p>Generating alignment analysis…</p>
          </div>

          <p v-else-if="alignment.error" class="member-profile__inline-error">{{ alignment.error }}</p>

          <template v-else-if="alignment.data">
            <template v-if="alignment.data.noGoals">
              <p v-if="isSelf" class="member-profile__empty-inline">
                Add development goals above to generate your Work-to-Growth Alignment.
              </p>
              <p v-else class="member-profile__empty-inline">
                No alignment available — no goals have been entered for this profile.
              </p>
            </template>

            <template v-else>
              <p v-if="alignment.data.answer" class="member-profile__alignment-summary">
                {{ alignment.data.answer }}
              </p>

              <template v-if="alignment.data.coachingBrief">
                <div v-if="alignment.data.coachingBrief.observedThemes && alignment.data.coachingBrief.observedThemes.length" class="member-profile__alignment-block">
                  <h3 class="member-profile__alignment-block-title">Where work connects to your goals</h3>
                  <ul class="member-profile__alignment-list">
                    <li v-for="(item, idx) in alignment.data.coachingBrief.observedThemes" :key="'ot-' + idx">{{ item }}</li>
                  </ul>
                </div>

                <div v-if="alignment.data.coachingBrief.strengthsToReinforce && alignment.data.coachingBrief.strengthsToReinforce.length" class="member-profile__alignment-block">
                  <h3 class="member-profile__alignment-block-title">Strengths to build on</h3>
                  <ul class="member-profile__alignment-list">
                    <li v-for="(item, idx) in alignment.data.coachingBrief.strengthsToReinforce" :key="'str-' + idx">{{ item }}</li>
                  </ul>
                </div>

                <div v-if="alignment.data.coachingBrief.concernsToAddress && alignment.data.coachingBrief.concernsToAddress.length" class="member-profile__alignment-block">
                  <h3 class="member-profile__alignment-block-title">Where goal exposure is missing</h3>
                  <ul class="member-profile__alignment-list">
                    <li v-for="(item, idx) in alignment.data.coachingBrief.concernsToAddress" :key="'cta-' + idx">{{ item }}</li>
                  </ul>
                </div>

                <div v-if="alignment.data.coachingBrief.talkingPoints && alignment.data.coachingBrief.talkingPoints.length" class="member-profile__alignment-block">
                  <h3 class="member-profile__alignment-block-title">Discussion prompts</h3>
                  <ul class="member-profile__alignment-list">
                    <li v-for="(item, idx) in alignment.data.coachingBrief.talkingPoints" :key="'tp-' + idx">{{ item }}</li>
                  </ul>
                </div>

                <div v-if="alignment.data.coachingBrief.followupPlan && alignment.data.coachingBrief.followupPlan.length" class="member-profile__alignment-block">
                  <h3 class="member-profile__alignment-block-title">What to verify</h3>
                  <ul class="member-profile__alignment-list">
                    <li v-for="(item, idx) in alignment.data.coachingBrief.followupPlan" :key="'fp-' + idx">{{ item }}</li>
                  </ul>
                </div>
              </template>

              <div v-if="alignment.data.limitations && alignment.data.limitations.length" class="member-profile__alignment-context">
                <h3 class="member-profile__alignment-context-title">Data context</h3>
                <ul class="member-profile__wr-context-list">
                  <li v-for="(lim, idx) in alignment.data.limitations" :key="'lim-' + idx">{{ lim }}</li>
                </ul>
              </div>
            </template>
          </template>
        </div>

        <!-- 1:1 Prep -->
        <div v-if="canViewPrep && !previewMode" class="member-profile__section tw-card">
          <div class="member-profile__section-header">
            <h2 class="member-profile__section-title">1:1 Prep</h2>
            <b-button
              v-if="!prep.loading"
              type="is-primary"
              size="is-small"
              @click="generate1on1Prep"
            >{{ prep.data ? 'Regenerate' : 'Generate 1:1 Prep' }}</b-button>
          </div>

          <div v-if="!prep.loading" class="member-profile__prep-template">
            <label class="member-profile__prep-template-label">Focus areas (optional)</label>
            <b-input
              v-model="prep.template"
              type="textarea"
              rows="2"
              placeholder="e.g. Focus on delivery blockers and upcoming PTO impact"
              :disabled="prep.loading"
            />
          </div>

          <div v-if="prep.loading" class="member-profile__prep-loading">
            <b-loading :is-full-page="false" :active="true" />
            <p class="member-profile__prep-status">{{ prep.status || 'Generating…' }}</p>
          </div>

          <p v-if="prep.error" class="member-profile__inline-error">{{ prep.error }}</p>

          <template v-if="prep.data && !prep.loading">
            <template v-if="prep.data.coachingBrief">
              <div v-if="prep.data.coachingBrief.coachingObjective" class="member-profile__prep-block">
                <h3 class="member-profile__prep-block-title">Main takeaway</h3>
                <p class="member-profile__prep-summary">{{ prep.data.coachingBrief.coachingObjective }}</p>
              </div>

              <div v-if="prep.data.coachingBrief.observedThemes && prep.data.coachingBrief.observedThemes.length" class="member-profile__prep-block">
                <h3 class="member-profile__prep-block-title">Work observations</h3>
                <ul class="member-profile__prep-list">
                  <li v-for="(theme, idx) in prep.data.coachingBrief.observedThemes" :key="'ot-' + idx">{{ theme }}</li>
                </ul>
              </div>

              <div v-if="prep.data.coachingBrief.talkingPoints && prep.data.coachingBrief.talkingPoints.length" class="member-profile__prep-block">
                <h3 class="member-profile__prep-block-title">Discussion questions</h3>
                <ul class="member-profile__prep-list">
                  <li v-for="(pt, idx) in prep.data.coachingBrief.talkingPoints" :key="'tp-' + idx">{{ pt }}</li>
                </ul>
              </div>

              <div v-if="prep.data.coachingBrief.coachingActions && prep.data.coachingBrief.coachingActions.length" class="member-profile__prep-block">
                <h3 class="member-profile__prep-block-title">Possible next steps</h3>
                <ul class="member-profile__prep-list">
                  <li v-for="(action, idx) in prep.data.coachingBrief.coachingActions" :key="'ca-' + idx">{{ action }}</li>
                </ul>
              </div>

              <div v-if="prep.data.coachingBrief.followupPlan && prep.data.coachingBrief.followupPlan.length" class="member-profile__prep-block">
                <h3 class="member-profile__prep-block-title">What to verify</h3>
                <ul class="member-profile__prep-list">
                  <li v-for="(item, idx) in prep.data.coachingBrief.followupPlan" :key="'fp-' + idx">{{ item }}</li>
                </ul>
              </div>

              <div v-if="prep.data.coachingBrief.evidenceAndCaveats && prep.data.coachingBrief.evidenceAndCaveats.length" class="member-profile__prep-block member-profile__prep-context">
                <h3 class="member-profile__prep-block-title">Evidence &amp; caveats</h3>
                <ul class="member-profile__prep-list">
                  <li v-for="(ev, idx) in prep.data.coachingBrief.evidenceAndCaveats" :key="'ev-' + idx">{{ ev }}</li>
                </ul>
              </div>
            </template>

            <div v-if="prep.data.limitations && prep.data.limitations.length" class="member-profile__prep-context">
              <h3 class="member-profile__prep-block-title">Data context</h3>
              <ul class="member-profile__wr-context-list">
                <li v-for="(lim, idx) in prep.data.limitations" :key="'lim-' + idx">{{ lim }}</li>
              </ul>
            </div>
          </template>

          <p v-if="!prep.data && !prep.loading && !prep.error" class="member-profile__empty-inline">
            Click "Generate 1:1 Prep" to create a coaching brief for this member.
          </p>
        </div>

        <div class="member-profile__two-col">
        <!-- Skills -->
        <div v-if="!previewMode || publicSettings.visibleFields.includes('skills')" class="member-profile__section tw-card">
          <h2 class="member-profile__section-title">Skills</h2>

          <div v-if="profile.skills && profile.skills.length" class="member-profile__tags">
            <span
              v-for="skill in profile.skills"
              :key="skill._id"
              class="member-profile__tag"
            >
              {{ skill.name }}
              <button
                v-if="isSelf && !previewMode"
                class="member-profile__tag-remove"
                @click="removeSkill(skill._id)"
                aria-label="Remove skill"
              >×</button>
            </span>
          </div>
          <p v-else class="member-profile__empty-inline">
            {{ isSelf && !previewMode ? 'No skills added yet.' : 'No skills listed.' }}
          </p>

          <div v-if="isSelf && !previewMode" class="member-profile__add-row">
            <b-input
              v-model="skills.newName"
              placeholder="Add a skill…"
              @keyup.enter="addSkill"
            />
            <b-button
              type="is-primary"
              size="is-small"
              :loading="skills.saving"
              :disabled="!skills.newName.trim()"
              @click="addSkill"
            >Add</b-button>
          </div>
          <p v-if="skills.error" class="member-profile__inline-error">{{ skills.error }}</p>
        </div>

        <!-- Certifications -->
        <div v-if="!previewMode || publicSettings.visibleFields.includes('certifications')" class="member-profile__section tw-card">
          <div class="member-profile__section-header">
            <h2 class="member-profile__section-title">Certifications</h2>
            <b-button
              v-if="isSelf && !certs.adding && !previewMode"
              size="is-small"
              type="is-light"
              icon-left="plus"
              @click="certs.adding = true"
            >Add</b-button>
          </div>

          <div v-if="profile.certifications && profile.certifications.length" class="member-profile__cert-list">
            <div
              v-for="cert in profile.certifications"
              :key="cert._id"
              class="member-profile__cert"
            >
              <template v-if="certs.editingId === cert._id">
                <b-field>
                  <b-input v-model="certs.editForm.name" placeholder="Certification name" />
                </b-field>
                <b-field>
                  <b-input v-model="certs.editForm.issuingOrganization" placeholder="Issuing organization" />
                </b-field>
                <b-field label="Expiry date (optional)">
                  <b-input v-model="certs.editForm.expiryDate" type="date" />
                </b-field>
                <div class="member-profile__cert-actions">
                  <b-button size="is-small" type="is-primary" :loading="certs.saving" @click="saveEditCert(cert._id)">Save</b-button>
                  <b-button size="is-small" type="is-text" :disabled="certs.saving" @click="certs.editingId = null">Cancel</b-button>
                </div>
              </template>
              <template v-else>
                <div class="member-profile__cert-info">
                  <span class="member-profile__cert-name">{{ cert.name }}</span>
                  <span v-if="cert.issuingOrganization" class="member-profile__cert-org">{{ cert.issuingOrganization }}</span>
                  <span v-if="cert.expiryDate" class="member-profile__cert-expiry">
                    Expires {{ new Date(cert.expiryDate).toLocaleDateString() }}
                  </span>
                </div>
                <div v-if="isSelf && !previewMode" class="member-profile__cert-actions">
                  <b-button size="is-small" type="is-light" icon-left="pencil" @click="startEditCert(cert)">Edit</b-button>
                  <b-button size="is-small" type="is-light" icon-left="delete" @click="removeCertification(cert._id)">Remove</b-button>
                </div>
              </template>
            </div>
          </div>
          <p v-else class="member-profile__empty-inline">
            {{ isSelf && !previewMode ? 'No certifications added yet.' : 'No certifications listed.' }}
          </p>

          <div v-if="isSelf && certs.adding" class="member-profile__cert-form">
            <b-field label="Certification name *">
              <b-input v-model="certs.form.name" placeholder="e.g. AWS Solutions Architect Associate" />
            </b-field>
            <b-field label="Issuing organization">
              <b-input v-model="certs.form.issuingOrganization" placeholder="e.g. Amazon Web Services" />
            </b-field>
            <b-field label="Expiry date (optional)">
              <b-input v-model="certs.form.expiryDate" type="date" />
            </b-field>
            <p v-if="certs.error" class="member-profile__inline-error">{{ certs.error }}</p>
            <div class="member-profile__cert-actions">
              <b-button type="is-primary" size="is-small" :loading="certs.saving" :disabled="!certs.form.name.trim()" @click="addCertification">Save</b-button>
              <b-button type="is-text" size="is-small" :disabled="certs.saving" @click="certs.adding = false; certs.error = ''">Cancel</b-button>
            </div>
          </div>
        </div>
        </div><!-- /.member-profile__two-col (skills + certs) -->

        <!-- Career Highlights -->
        <div
          v-if="resumeData.workHistory.length && (!previewMode || publicSettings.visibleFields.includes('career_highlights'))"
          class="member-profile__section tw-card"
        >
          <h2 class="member-profile__section-title">Career Highlights</h2>
          <div class="member-profile__experience-list">
            <div
              v-for="(job, idx) in resumeData.workHistory"
              :key="idx"
              class="member-profile__experience-item"
            >
              <div class="member-profile__experience-period">
                {{ formatExpDate(job.startDate) }} — {{ job.endDate ? formatExpDate(job.endDate) : 'Present' }}
              </div>
              <p class="member-profile__experience-role">{{ job.position }}</p>
              <p class="member-profile__experience-company">{{ job.company || job.organization }}</p>
              <p v-if="job.description" class="member-profile__experience-desc">{{ job.description }}</p>
            </div>
          </div>
        </div>

        <!-- Work Preferences + Team Preferences -->
        <div
          v-if="resumeData.workPreferences || resumeData.teamPreferences"
          class="member-profile__two-col"
        >
          <div
            v-if="resumeData.workPreferences && (!previewMode || publicSettings.visibleFields.includes('work_preferences'))"
            class="member-profile__section tw-card"
          >
            <h2 class="member-profile__section-title">Work Preferences</h2>
            <p class="member-profile__summary-text">{{ resumeData.workPreferences }}</p>
          </div>
          <div
            v-if="resumeData.teamPreferences && (!previewMode || publicSettings.visibleFields.includes('team_preferences'))"
            class="member-profile__section tw-card"
          >
            <h2 class="member-profile__section-title">Team Preferences</h2>
            <p class="member-profile__summary-text">{{ resumeData.teamPreferences }}</p>
          </div>
        </div>

        <!-- Soft Skills + Core Values -->
        <div
          v-if="resumeData.softSkills.length || resumeData.values.length"
          class="member-profile__two-col"
        >
          <div
            v-if="resumeData.softSkills.length && (!previewMode || publicSettings.visibleFields.includes('soft_skills'))"
            class="member-profile__section tw-card"
          >
            <h2 class="member-profile__section-title">Soft Skills</h2>
            <div class="member-profile__pref-tags">
              <span v-for="skill in resumeData.softSkills" :key="skill" class="member-profile__pref-tag">{{ skill }}</span>
            </div>
          </div>
          <div
            v-if="resumeData.values.length && (!previewMode || publicSettings.visibleFields.includes('core_values'))"
            class="member-profile__section tw-card"
          >
            <h2 class="member-profile__section-title">Core Values</h2>
            <div class="member-profile__pref-tags">
              <span v-for="value in resumeData.values" :key="value" class="member-profile__pref-tag member-profile__pref-tag--value">{{ value }}</span>
            </div>
          </div>
        </div>

        <!-- Public Profile Settings (own profile only, hidden in preview mode) -->
        <div v-if="isSelf && !previewMode" class="member-profile__section tw-card">
          <h2 class="member-profile__section-title">Public Profile</h2>

          <div class="member-profile__public-toggle-row">
            <label class="member-profile__public-toggle-label">
              <b-switch
                v-model="publicSettings.publicEnabled"
                :disabled="publicSettingsSaving"
                @update:modelValue="togglePublicEnabled"
              />
              <span>{{ publicSettings.publicEnabled ? 'Public profile is on' : 'Public profile is off' }}</span>
            </label>
            <div v-if="publicSettings.publicEnabled" class="member-profile__public-link-row">
              <span class="member-profile__public-link-url">{{ publicProfileUrl }}</span>
              <b-button
                type="is-light"
                size="is-small"
                :icon-left="copyLinkSuccess ? 'check' : 'link-variant'"
                @click="copyPublicLink"
              >
                {{ copyLinkSuccess ? 'Copied!' : 'Copy link' }}
              </b-button>
            </div>
          </div>

          <template v-if="publicSettings.publicEnabled">
            <p class="member-profile__public-section-label">Choose what appears on your public profile:</p>
            <div class="member-profile__public-field-list">
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('summary')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('summary')"
                />
                Professional Summary
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('skills')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('skills')"
                />
                Skills
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('certifications')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('certifications')"
                />
                Certifications
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('career_highlights')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('career_highlights')"
                />
                Career Highlights
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('work_preferences')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('work_preferences')"
                />
                Work Preferences
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('team_preferences')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('team_preferences')"
                />
                Team Preferences
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('soft_skills')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('soft_skills')"
                />
                Soft Skills
              </label>
              <label class="member-profile__public-field-item">
                <b-checkbox
                  :modelValue="publicSettings.visibleFields.includes('core_values')"
                  :disabled="publicSettingsSaving"
                  @update:modelValue="togglePublicField('core_values')"
                />
                Core Values
              </label>
            </div>
          </template>

          <p v-if="publicSettingsError" class="member-profile__inline-error">{{ publicSettingsError }}</p>
        </div>

        <div v-if="(isAdminViewingOther || (isSelf && user?.isAdmin)) && !previewMode" class="member-profile__section tw-card">
          <h2 class="member-profile__section-title">PSA Mapping</h2>

          <div v-if="psa.saved" class="member-profile__psa-success">
            <i class="mdi mdi-check-circle"></i>
            Mapped to <strong>{{ psa.selected.memberName }}</strong> ({{ psa.selected.memberIdentifier }})
          </div>

          <template v-else>
            <div v-if="profile.psaMemberId" class="member-profile__psa-current">
              <i class="mdi mdi-check-circle member-profile__psa-mapped-icon"></i>
              <span>Currently mapped: <strong>{{ profile.psaMemberId }}</strong></span>
            </div>

            <p class="member-profile__psa-hint">Search PSA members by name to find and link this account.</p>
            <div class="member-profile__psa-search-row">
              <b-input v-model="psa.query" placeholder="Search by name…" @keyup.enter="runPsaSearch" />
              <b-button type="is-primary" :loading="psa.searching" :disabled="!psa.query.trim()" @click="runPsaSearch">
                Search
              </b-button>
            </div>

            <p v-if="psa.error" class="member-profile__psa-error">{{ psa.error }}</p>

            <div v-if="psa.searched && !psa.results.length && !psa.searching" class="member-profile__psa-empty">
              No PSA members found matching "{{ psa.query }}".
            </div>

            <div v-if="psa.results.length" class="member-profile__psa-results">
              <div
                v-for="candidate in psa.results"
                :key="candidate.memberIdentifier"
                class="member-profile__psa-candidate"
                :class="{ 'member-profile__psa-candidate--selected': psa.selected?.memberIdentifier === candidate.memberIdentifier }"
                @click="psa.selected = candidate"
              >
                <div class="member-profile__psa-candidate-name">{{ candidate.memberName }}</div>
                <div class="member-profile__psa-candidate-id">ID: {{ candidate.memberIdentifier }}</div>
              </div>
            </div>

            <div v-if="psa.selected" class="member-profile__psa-confirm">
              <p>Confirm mapping to <strong>{{ psa.selected.memberName }}</strong> ({{ psa.selected.memberIdentifier }})?</p>
              <b-button type="is-primary" :loading="psa.saving" @click="confirmPsaMapping">Confirm Mapping</b-button>
              <b-button type="is-text" :disabled="psa.saving" @click="psa.selected = null">Cancel</b-button>
            </div>
          </template>
        </div>

      </template>

    </div>
  </section>
</template>

<script>
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'
import {
  fetchMemberProfile, fetchCompanyName, patchMemberProfile, setupProfile,
  searchPsaMembers, savePsaMapping, fetchWorkRecord, fetchAlignment, stream1on1Prep,
  savePublicSettings,
  addProfileSkill, removeProfileSkill,
  addProfileCertification, updateProfileCertification, removeProfileCertification,
  addProfileGoal, updateProfileGoal, removeProfileGoal,
} from '@/services/profileService'

const ROLE_LABELS = {
  individual: 'Individual',
  manager: 'Manager',
  executive: 'Executive',
};

export default {
  name: 'MemberProfile',
  data() {
    return {
      profile: null,
      viewerRole: 'public',
      companyName: null,
      isLoading: false,
      notFound: false,
      isPrivate: false,
      initProfile: {
        role: 'individual',
        displayName: '',
        title: '',
        saving: false,
        error: '',
      },
      isEditing: false,
      isSaving: false,
      editError: '',
      editForm: {
        displayName: '',
        title: '',
      },
      psa: {
        query: '',
        searching: false,
        results: [],
        searched: false,
        selected: null,
        saving: false,
        saved: false,
        error: '',
      },
      wr: {
        data: null,
        loading: false,
        error: '',
        days: 30,
      },
      skills: {
        newName: '',
        saving: false,
        error: '',
      },
      certs: {
        adding: false,
        form: { name: '', issuingOrganization: '', expiryDate: '' },
        saving: false,
        error: '',
        editingId: null,
        editForm: { name: '', issuingOrganization: '', expiryDate: '' },
      },
      goals: {
        newText: '',
        saving: false,
        error: '',
        editingId: null,
        editText: '',
      },
      alignment: {
        data: null,
        loading: false,
        error: '',
      },
      prep: {
        data: null,
        loading: false,
        status: '',
        error: '',
        template: '',
      },
      resumeData: {
        summary: null,
        workHistory: [],
        workPreferences: '',
        teamPreferences: '',
        softSkills: [],
        values: [],
      },
      publicSettings: {
        publicEnabled: false,
        visibleFields: [],
      },
      publicSettingsSaving: false,
      publicSettingsError: '',
      previewMode: false,
      copyLinkSuccess: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['user']),
    roleLabel() {
      return ROLE_LABELS[this.profile?.role] || this.profile?.role || '—';
    },
    isSelf() {
      return this.user?._id && String(this.user._id) === String(this.$route.params.userId);
    },
    isAdminViewingOther() {
      return this.user?.isAdmin && !this.isSelf;
    },
    canViewAlignment() {
      return this.viewerRole === 'individual' || this.viewerRole === 'manager' || this.viewerRole === 'admin';
    },
    canViewPrep() {
      return this.viewerRole === 'manager' || this.viewerRole === 'admin';
    },
    publicProfileUrl() {
      const { userId } = this.$route.params;
      return `${window.location.origin}/member/${userId}`;
    },
    workRecordFrom() {
      const d = new Date();
      d.setDate(d.getDate() - this.wr.days);
      return d.toISOString().slice(0, 10);
    },
    workRecordTo() {
      return new Date().toISOString().slice(0, 10);
    },
  },
  async mounted() {
    const token = this.$cookies.get('teamwrkr-token');
    if (token) {
      const userStore = useUserStore();
      await userStore.fetchUser(token);
    }
    await this.loadProfile();
  },
  methods: {
    async loadProfile() {
      this.isLoading = true;
      this.notFound = false;
      this.isPrivate = false;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;

      try {
        const result = await fetchMemberProfile(this.axios, token, userId);
        this.profile = result.profile;
        this.viewerRole = result.viewerRole;
        if (result.viewerRole === 'individual' || result.viewerRole === 'admin') {
          this.publicSettings = {
            publicEnabled: result.profile.publicEnabled ?? false,
            visibleFields: result.profile.visibleFields ?? [],
          };
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          this.isPrivate = true;
        } else {
          this.notFound = true;
        }
        return;
      } finally {
        this.isLoading = false;
      }

      if (this.viewerRole !== 'public') {
        this.loadWorkRecord();
        this.loadAlignment();
      }
      this.fetchResumeData();

      const companyId = typeof this.user?.company === 'object'
        ? this.user.company._id
        : this.user?.company;

      if (companyId) {
        try {
          this.companyName = await fetchCompanyName(this.axios, token, companyId);
        } catch {
          // non-blocking — company name is display-only
        }
      }
    },

    async initializeMemberProfile() {
      this.initProfile.error = '';
      this.initProfile.saving = true;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        await setupProfile(this.axios, token, userId, {
          role: this.initProfile.role,
          displayName: this.initProfile.displayName.trim() || undefined,
          title: this.initProfile.title.trim() || undefined,
        });
        this.notFound = false;
        await this.loadProfile();
      } catch (err) {
        this.initProfile.error = err?.response?.data?.message || 'Failed to initialize profile.';
      } finally {
        this.initProfile.saving = false;
      }
    },

    async loadWorkRecord() {
      this.wr.loading = true;
      this.wr.error = '';
      this.wr.data = null;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        this.wr.data = await fetchWorkRecord(this.axios, token, userId, {
          from: this.workRecordFrom,
          to: this.workRecordTo,
        });
      } catch {
        this.wr.error = 'Failed to load work record.';
      } finally {
        this.wr.loading = false;
      }
    },

    selectDays(days) {
      this.wr.days = days;
      this.loadWorkRecord();
    },

    async generate1on1Prep() {
      if (!this.canViewPrep || this.prep.loading) return;
      this.prep.loading = true;
      this.prep.error = '';
      this.prep.status = 'Preparing…';
      this.prep.data = null;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const result = await stream1on1Prep(token, userId, {
          from: this.workRecordFrom,
          to: this.workRecordTo,
          template: this.prep.template.trim() || undefined,
        }, {
          onStatus: (msg) => { if (msg) this.prep.status = msg; },
          onFinal: (data) => { this.prep.data = data; },
          onError: (err) => { this.prep.error = err?.message || 'Generation failed.'; },
        });
        if (result && !this.prep.data) this.prep.data = result;
      } catch (err) {
        if (err?.status === 403) {
          this.prep.error = '1:1 Prep is only available to the direct manager.';
        } else {
          this.prep.error = err?.message || 'Failed to generate 1:1 Prep.';
        }
      } finally {
        this.prep.loading = false;
        this.prep.status = '';
      }
    },

    async persistPublicSettings(patch) {
      this.publicSettingsSaving = true;
      this.publicSettingsError = '';
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await savePublicSettings(this.axios, token, userId, patch);
        this.publicSettings = {
          publicEnabled: updated.publicEnabled ?? false,
          visibleFields: updated.visibleFields ?? [],
        };
      } catch (err) {
        this.publicSettingsError = err?.message || 'Failed to save settings.';
      } finally {
        this.publicSettingsSaving = false;
      }
    },

    togglePublicEnabled(newValue) {
      this.persistPublicSettings({ publicEnabled: newValue, visibleFields: this.publicSettings.visibleFields });
    },

    togglePublicField(field) {
      const fields = [...(this.publicSettings.visibleFields || [])];
      const idx = fields.indexOf(field);
      if (idx === -1) fields.push(field);
      else fields.splice(idx, 1);
      this.publicSettings = { ...this.publicSettings, visibleFields: fields };
      clearTimeout(this._publicFieldsSaveTimer);
      this._publicFieldsSaveTimer = setTimeout(() => {
        this.persistPublicSettings({
          publicEnabled: this.publicSettings.publicEnabled,
          visibleFields: [...this.publicSettings.visibleFields],
        });
      }, 500);
    },

    async copyPublicLink() {
      try {
        await navigator.clipboard.writeText(this.publicProfileUrl);
        this.copyLinkSuccess = true;
        setTimeout(() => { this.copyLinkSuccess = false; }, 2000);
      } catch {
        this.publicSettingsError = 'Could not copy to clipboard.';
      }
    },

    async loadAlignment() {
      if (!this.canViewAlignment) return;
      this.alignment.loading = true;
      this.alignment.error = '';
      this.alignment.data = null;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        this.alignment.data = await fetchAlignment(this.axios, token, userId, {
          from: this.workRecordFrom,
          to: this.workRecordTo,
        });
      } catch {
        this.alignment.error = 'Failed to load Work-to-Growth Alignment.';
      } finally {
        this.alignment.loading = false;
      }
    },

    async addSkill() {
      if (!this.skills.newName.trim()) return;
      this.skills.saving = true;
      this.skills.error = '';
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await addProfileSkill(this.axios, token, userId, this.skills.newName.trim());
        this.profile = { ...this.profile, skills: updated.skills };
        this.skills.newName = '';
      } catch {
        this.skills.error = 'Failed to add skill.';
      } finally {
        this.skills.saving = false;
      }
    },

    async removeSkill(skillId) {
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await removeProfileSkill(this.axios, token, userId, skillId);
        this.profile = { ...this.profile, skills: updated.skills };
      } catch {
        this.$buefy.toast.open({ message: 'Failed to remove skill.', type: 'is-danger' });
      }
    },

    async addCertification() {
      if (!this.certs.form.name.trim()) { this.certs.error = 'Name is required.'; return; }
      this.certs.saving = true;
      this.certs.error = '';
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await addProfileCertification(this.axios, token, userId, {
          name: this.certs.form.name.trim(),
          issuingOrganization: this.certs.form.issuingOrganization.trim() || null,
          expiryDate: this.certs.form.expiryDate || null,
        });
        this.profile = { ...this.profile, certifications: updated.certifications };
        this.certs.form = { name: '', issuingOrganization: '', expiryDate: '' };
        this.certs.adding = false;
      } catch {
        this.certs.error = 'Failed to add certification.';
      } finally {
        this.certs.saving = false;
      }
    },

    startEditCert(cert) {
      this.certs.editingId = cert._id;
      this.certs.editForm = {
        name: cert.name,
        issuingOrganization: cert.issuingOrganization || '',
        expiryDate: cert.expiryDate ? new Date(cert.expiryDate).toISOString().slice(0, 10) : '',
      };
    },

    async saveEditCert(certId) {
      if (!this.certs.editForm.name.trim()) return;
      this.certs.saving = true;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await updateProfileCertification(this.axios, token, userId, certId, {
          name: this.certs.editForm.name.trim(),
          issuingOrganization: this.certs.editForm.issuingOrganization.trim() || null,
          expiryDate: this.certs.editForm.expiryDate || null,
        });
        this.profile = { ...this.profile, certifications: updated.certifications };
        this.certs.editingId = null;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to update certification.', type: 'is-danger' });
      } finally {
        this.certs.saving = false;
      }
    },

    async removeCertification(certId) {
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await removeProfileCertification(this.axios, token, userId, certId);
        this.profile = { ...this.profile, certifications: updated.certifications };
      } catch {
        this.$buefy.toast.open({ message: 'Failed to remove certification.', type: 'is-danger' });
      }
    },

    async addGoal() {
      if (!this.goals.newText.trim()) return;
      this.goals.saving = true;
      this.goals.error = '';
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await addProfileGoal(this.axios, token, userId, this.goals.newText.trim());
        this.profile = { ...this.profile, goals: updated.goals };
        this.goals.newText = '';
      } catch {
        this.goals.error = 'Failed to add goal.';
      } finally {
        this.goals.saving = false;
      }
    },

    startEditGoal(goal) {
      this.goals.editingId = goal._id;
      this.goals.editText = goal.text;
    },

    async saveGoal(goalId) {
      if (!this.goals.editText.trim()) return;
      this.goals.saving = true;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await updateProfileGoal(this.axios, token, userId, goalId, this.goals.editText.trim());
        this.profile = { ...this.profile, goals: updated.goals };
        this.goals.editingId = null;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to update goal.', type: 'is-danger' });
      } finally {
        this.goals.saving = false;
      }
    },

    cancelEditGoal() {
      this.goals.editingId = null;
      this.goals.editText = '';
    },

    async removeGoal(goalId) {
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        const updated = await removeProfileGoal(this.axios, token, userId, goalId);
        this.profile = { ...this.profile, goals: updated.goals };
      } catch {
        this.$buefy.toast.open({ message: 'Failed to remove goal.', type: 'is-danger' });
      }
    },

    startEdit() {
      this.editForm = {
        displayName: this.profile.displayName || '',
        title: this.profile.title || '',
      };
      this.editError = '';
      this.isEditing = true;
    },

    cancelEdit() {
      this.isEditing = false;
      this.editError = '';
    },

    async runPsaSearch() {
      if (!this.psa.query.trim()) return;
      this.psa.searching = true;
      this.psa.error = '';
      this.psa.results = [];
      this.psa.selected = null;
      this.psa.searched = false;
      const token = this.$cookies.get('teamwrkr-token');
      try {
        this.psa.results = await searchPsaMembers(this.axios, token, this.psa.query.trim());
        this.psa.searched = true;
      } catch {
        this.psa.error = 'Search failed. Please try again.';
      } finally {
        this.psa.searching = false;
      }
    },

    async confirmPsaMapping() {
      if (!this.psa.selected) return;
      this.psa.saving = true;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;
      try {
        await savePsaMapping(this.axios, token, userId, this.psa.selected.memberIdentifier);
        this.psa.saved = true;
        if (this.profile) this.profile.psaMemberId = this.psa.selected.memberIdentifier;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to save PSA mapping. Please try again.', type: 'is-danger' });
      } finally {
        this.psa.saving = false;
      }
    },

    async saveProfile() {
      if (!this.editForm.displayName.trim()) {
        this.editError = 'Display name cannot be empty.';
        return;
      }

      this.isSaving = true;
      const token = this.$cookies.get('teamwrkr-token');
      const { userId } = this.$route.params;

      try {
        const updated = await patchMemberProfile(this.axios, token, userId, {
          displayName: this.editForm.displayName.trim(),
          title: this.editForm.title.trim(),
        });
        this.profile = { ...this.profile, ...updated };
        this.isEditing = false;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to save profile. Please try again.', type: 'is-danger' });
      } finally {
        this.isSaving = false;
      }
    },

    async fetchResumeData() {
      const { userId } = this.$route.params;
      const token = this.$cookies.get('teamwrkr-token');
      const url = `${process.env.VUE_APP_API_URL}/users/statsheet/${userId}`;
      try {
        // A stale/invalid login gets a 401; retry once without it for the public view.
        const res = await this.axios.get(url, { headers: { Authorization: token } })
          .catch(error => (error?.response?.status === 401 ? this.axios.get(url) : Promise.reject(error)));
        const analysis = res.data?.resumeAnalysis || {};
        this.resumeData.summary = analysis.summary || null;
        this.resumeData.workHistory = res.data?.workHistory || [];
        this.resumeData.workPreferences = analysis.workPreferences || '';
        this.resumeData.teamPreferences = analysis.teamPreferences || '';
        this.resumeData.softSkills = analysis.softSkills || [];
        this.resumeData.values = analysis.values || [];
      } catch {
        // non-blocking — resume data is supplementary
      }
    },

    formatExpDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style lang="scss" scoped>
.member-profile {
  &__not-found {
    flex-direction: column;
    gap: $space-3;
    text-align: center;
    padding: $space-8 $space-4;
  }

  &__not-found-icon {
    font-size: 3rem;
    color: $color-text-subtle;
  }

  &__not-found-title {
    font-size: $font-size-lg;
    font-weight: 600;
    color: $color-text;
  }

  &__not-found-sub {
    color: $color-text-muted;
  }

  &__init-form {
    margin-top: $space-4;
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: $space-3;
    text-align: left;
  }

  &__init-error {
    color: $color-danger;
    font-size: $font-size-sm;
    margin: 0;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $space-4;
    flex-wrap: wrap;
    margin-bottom: $space-5;
  }

  &__identity {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    flex: 1;
    min-width: 0;
  }

  &__edit-actions {
    display: flex;
    gap: $space-2;
    margin-top: $space-1;
  }

  &__name {
    font-size: $font-size-xl;
    font-weight: 700;
    color: $color-text-strong;
    margin: 0;
  }

  &__title {
    font-size: $font-size-md;
    color: $color-text-muted;
    margin: 0;
  }

  &__company {
    font-size: $font-size-sm;
    color: $color-text-subtle;
    display: flex;
    align-items: center;
    gap: $space-1;
    margin: 0;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: $space-2;
    flex-shrink: 0;
  }

  &__role-badge {
    display: inline-block;
    padding: 0.2rem 0.75rem;
    border-radius: $radius-pill;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &--individual {
      background: $color-highlight;
      color: $color-primary-dark;
    }

    &--manager {
      background: $color-info-soft;
      color: $color-info;
    }

    &--executive {
      background: $color-success-soft;
      color: $color-success-strong;
    }
  }

  &__public-badge {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    padding: 0.2rem 0.65rem;
    border-radius: $radius-pill;
    font-size: $font-size-xs;
    font-weight: 600;

    &--on {
      background: $color-success-soft;
      color: $color-success-strong;
    }

    &--off {
      background: $color-surface-muted;
      color: $color-text-subtle;
    }
  }

  &__section {
    margin-bottom: $space-5;
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $space-4;

    .member-profile__section-title {
      margin: 0;
    }
  }

  &__section-title {
    font-size: $font-size-lg;
    font-weight: 700;
    color: $color-text-strong;
    margin: 0 0 $space-4;
  }

  &__wr-range {
    display: flex;
    gap: $space-1;
  }

  &__wr-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-2;
    padding: $space-6 $space-4;
    color: $color-text-muted;
    position: relative;
    min-height: 5rem;
  }

  &__wr-error {
    color: $color-text-muted;
    font-size: $font-size-sm;
    padding: $space-3 0;
  }

  &__wr-warning {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: $space-2 $space-3;
    background: $color-highlight;
    color: $color-primary-dark;
    border-radius: $radius-md;
    font-size: $font-size-sm;
    margin-bottom: $space-3;

    &--info {
      background: $color-info-soft;
      color: $color-info;
    }
  }

  &__wr-stats {
    display: flex;
    gap: $space-6;
    margin-bottom: $space-5;
    flex-wrap: wrap;
  }

  &__wr-stat {
    display: flex;
    flex-direction: column;
    gap: $space-1;
  }

  &__wr-stat-value {
    font-size: $font-size-xl;
    font-weight: 700;
    color: $color-text-strong;
    line-height: 1;
  }

  &__wr-stat-label {
    font-size: $font-size-xs;
    color: $color-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__wr-group {
    margin-bottom: $space-5;
  }

  &__wr-group-title {
    font-size: $font-size-sm;
    font-weight: 700;
    color: $color-text-subtle;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 $space-3;
  }

  &__wr-rows {
    display: flex;
    flex-direction: column;
    gap: $space-2;
  }

  &__wr-row {
    display: flex;
    align-items: center;
    gap: $space-3;
    font-size: $font-size-sm;
  }

  &__wr-row-label {
    flex: 1;
    color: $color-text;
    font-weight: 500;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__wr-row-count {
    color: $color-text-muted;
    font-size: $font-size-xs;
    flex-shrink: 0;
  }

  &__wr-row-hours {
    color: $color-text;
    font-weight: 600;
    min-width: 3rem;
    text-align: right;
    flex-shrink: 0;
  }

  &__wr-row-pct {
    color: $color-primary;
    font-weight: 600;
    min-width: 2.5rem;
    text-align: right;
    flex-shrink: 0;
  }

  &__wr-examples {
    display: flex;
    flex-direction: column;
    gap: $space-2;
  }

  &__wr-example {
    display: flex;
    align-items: flex-start;
    gap: $space-3;
    padding: $space-3 $space-4;
    background: $color-surface-alt;
    border-radius: $radius-md;
    border: 1px solid $color-border;
  }

  &__wr-example-meta {
    display: flex;
    flex-direction: column;
    gap: $space-1;
    min-width: 5rem;
    flex-shrink: 0;
  }

  &__wr-example-nbr {
    font-size: $font-size-xs;
    font-weight: 700;
    color: $color-primary;
  }

  &__wr-example-cat,
  &__wr-example-type {
    font-size: $font-size-xs;
    color: $color-text-subtle;
  }

  &__wr-example-summary {
    flex: 1;
    font-size: $font-size-sm;
    color: $color-text;
    min-width: 0;
  }

  &__wr-example-hours {
    font-size: $font-size-sm;
    font-weight: 600;
    color: $color-text;
    flex-shrink: 0;
  }

  &__wr-context {
    padding-top: $space-4;
    border-top: 1px solid $color-border;
  }

  &__wr-limitations {
    list-style: disc;
    padding-left: $space-4;
    margin-top: $space-2;
    font-size: $font-size-xs;
    color: $color-text-muted;

    li {
      margin-bottom: $space-1;
    }
  }

  &__empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-2;
    padding: $space-6 $space-4;
    text-align: center;
  }

  &__empty-icon {
    font-size: 2.5rem;
    color: $color-text-subtle;
  }

  &__empty-title {
    font-size: $font-size-md;
    font-weight: 600;
    color: $color-text;
    margin: 0;
  }

  &__empty-sub {
    font-size: $font-size-sm;
    color: $color-text-muted;
    max-width: 36rem;
    margin: 0;
  }

  &__psa-current {
    display: flex;
    align-items: center;
    gap: $space-2;
    font-size: $font-size-sm;
    color: $color-success-strong;
    margin-bottom: $space-4;
  }

  &__psa-mapped-icon {
    color: $color-success;
  }

  &__psa-success {
    display: flex;
    align-items: center;
    gap: $space-2;
    color: $color-success-strong;
    font-size: $font-size-md;
  }

  &__psa-hint {
    font-size: $font-size-sm;
    color: $color-text-muted;
    margin-bottom: $space-3;
  }

  &__psa-search-row {
    display: flex;
    gap: $space-2;
    align-items: center;
    margin-bottom: $space-3;
  }

  &__psa-error {
    color: $color-danger;
    font-size: $font-size-sm;
    margin-bottom: $space-3;
  }

  &__psa-empty {
    color: $color-text-muted;
    font-size: $font-size-sm;
    padding: $space-3 0;
  }

  &__psa-results {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__psa-candidate {
    padding: $space-3 $space-4;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
      border-color: $color-primary;
      background: $color-highlight-strong;
    }

    &--selected {
      border-color: $color-primary;
      background: $color-highlight;
    }
  }

  &__psa-candidate-name {
    font-weight: 600;
    color: $color-text;
  }

  &__psa-candidate-id {
    font-size: $font-size-xs;
    color: $color-text-subtle;
  }

  &__psa-confirm {
    display: flex;
    align-items: center;
    gap: $space-3;
    padding: $space-3 $space-4;
    background: $color-surface-alt;
    border-radius: $radius-md;
    border: 1px solid $color-border;

    p {
      flex: 1;
      font-size: $font-size-sm;
      color: $color-text;
      margin: 0;
    }
  }

  // Work-to-Growth Alignment
  &__alignment-loading {
    display: flex;
    align-items: center;
    gap: $space-3;
    padding: $space-4 0;
    font-size: $font-size-sm;
    color: $color-text-muted;
  }

  &__alignment-summary {
    font-size: $font-size-sm;
    color: $color-text;
    line-height: 1.6;
    margin-bottom: $space-4;
    padding-bottom: $space-4;
    border-bottom: 1px solid $color-border;
  }

  &__alignment-block {
    margin-bottom: $space-4;
  }

  &__alignment-block-title {
    font-size: $font-size-sm;
    font-weight: 600;
    color: $color-text;
    margin-bottom: $space-2;
  }

  &__alignment-list {
    padding-left: $space-4;
    margin: 0;

    li {
      font-size: $font-size-sm;
      color: $color-text;
      line-height: 1.5;
      margin-bottom: $space-1;
    }
  }

  &__alignment-context {
    margin-top: $space-4;
    padding-top: $space-3;
    border-top: 1px solid $color-border;
  }

  &__alignment-context-title {
    font-size: $font-size-xs;
    font-weight: 600;
    color: $color-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: $space-2;
  }

  // 1:1 Prep
  &__prep-template {
    margin-bottom: $space-3;
  }

  &__prep-template-label {
    display: block;
    font-size: $font-size-sm;
    font-weight: 600;
    color: $color-text-muted;
    margin-bottom: $space-1;
  }

  &__prep-loading {
    display: flex;
    align-items: center;
    gap: $space-3;
    padding: $space-4 0;
    color: $color-text-muted;
  }

  &__prep-status {
    font-size: $font-size-sm;
    color: $color-text-muted;
    font-style: italic;
  }

  &__prep-summary {
    font-size: $font-size-md;
    line-height: 1.6;
    color: $color-text;
    margin: 0;
  }

  &__prep-block {
    margin-top: $space-4;

    &:first-child {
      margin-top: 0;
    }
  }

  &__prep-block-title {
    font-size: $font-size-xs;
    font-weight: 600;
    color: $color-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: $space-2;
  }

  &__prep-list {
    margin: 0;
    padding-left: $space-4;
    color: $color-text;
    font-size: $font-size-md;
    line-height: 1.6;

    li + li {
      margin-top: $space-1;
    }
  }

  &__prep-context {
    margin-top: $space-4;
    padding-top: $space-3;
    border-top: 1px solid $color-border;
  }

  // Two-column layout
  &__two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: $space-5;
    margin-bottom: $space-5;

    .member-profile__section {
      margin-bottom: 0;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  // Professional Summary
  &__summary-text {
    font-size: $font-size-md;
    line-height: 1.7;
    color: $color-text;
    margin: 0;
    white-space: pre-line;
  }

  // Career Highlights
  &__experience-list {
    display: flex;
    flex-direction: column;
    gap: $space-5;
  }

  &__experience-item {
    padding-left: $space-4;
    border-left: 2px solid $color-primary-soft;
    display: flex;
    flex-direction: column;
    gap: $space-1;
  }

  &__experience-period {
    font-size: $font-size-xs;
    font-weight: 600;
    color: $color-text-subtle;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__experience-role {
    font-size: $font-size-md;
    font-weight: 700;
    color: $color-text-strong;
    margin: 0;
  }

  &__experience-company {
    font-size: $font-size-sm;
    color: $color-text-muted;
    margin: 0;
  }

  &__experience-desc {
    font-size: $font-size-sm;
    color: $color-text;
    line-height: 1.6;
    margin: $space-1 0 0;
  }

  // Preferences / Soft Skills / Core Values tags
  &__pref-tags {
    display: flex;
    flex-wrap: wrap;
    gap: $space-2;
  }

  &__pref-tag {
    display: inline-flex;
    align-items: center;
    padding: $space-1 $space-3;
    background: $color-surface-alt;
    border: 1px solid $color-border;
    border-radius: $radius-pill;
    font-size: $font-size-sm;
    color: $color-text;
    font-weight: 500;

    &--value {
      background: $color-highlight;
      border-color: $color-primary-soft;
      color: $color-primary-dark;
    }
  }

  // Skills
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    padding: $space-1 $space-2;
    background: $color-highlight;
    border: 1px solid $color-primary-soft;
    border-radius: $radius-pill;
    font-size: $font-size-sm;
    color: $color-primary-dark;
    font-weight: 500;
  }

  &__tag-remove {
    background: none;
    border: none;
    cursor: pointer;
    color: $color-primary;
    font-size: $font-size-md;
    line-height: 1;
    padding: 0;
    margin-left: $space-1;
    opacity: 0.6;
    transition: opacity 0.15s;

    &:hover {
      opacity: 1;
    }
  }

  // Certifications
  &__cert-list {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__cert {
    display: flex;
    align-items: flex-start;
    gap: $space-3;
    padding: $space-3 $space-4;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    background: $color-surface;
  }

  &__cert-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: $space-1;
  }

  &__cert-name {
    font-weight: 600;
    font-size: $font-size-sm;
    color: $color-text;
  }

  &__cert-org {
    font-size: $font-size-xs;
    color: $color-text-muted;
  }

  &__cert-expiry {
    font-size: $font-size-xs;
    color: $color-text-subtle;
  }

  &__cert-actions {
    display: flex;
    gap: $space-2;
    align-items: center;
    flex-shrink: 0;
  }

  &__cert-form {
    padding: $space-4;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    background: $color-surface-alt;
    margin-bottom: $space-3;

    .b-field {
      margin-bottom: $space-3;
    }
  }

  // Goals
  &__goals {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__goal {
    display: flex;
    align-items: flex-start;
    gap: $space-3;
    padding: $space-3 $space-4;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    background: $color-surface;
  }

  &__goal-text {
    flex: 1;
    font-size: $font-size-sm;
    color: $color-text;
    margin: 0;
    line-height: 1.5;
  }

  &__goal-actions {
    display: flex;
    gap: $space-2;
    align-items: center;
    flex-shrink: 0;
  }

  &__goal-edit-field {
    flex: 1;
    margin: 0;
  }

  // Shared add-row / feedback
  &__add-row {
    display: flex;
    gap: $space-2;
    align-items: center;
    margin-top: $space-3;

    &--block {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__inline-error {
    font-size: $font-size-sm;
    color: $color-danger;
    margin-top: $space-1;
    margin-bottom: 0;
  }

  &__empty-inline {
    font-size: $font-size-sm;
    color: $color-text-muted;
    margin-bottom: $space-3;
  }

  // Preview mode banner
  &__preview-banner {
    display: flex;
    align-items: center;
    gap: $space-3;
    padding: $space-3 $space-4;
    background: $color-highlight;
    border: 1px solid $color-primary-soft;
    border-radius: $radius-md;
    margin-bottom: $space-4;
    font-size: $font-size-sm;
    color: $color-text;
    flex-wrap: wrap;
  }

  &__preview-disabled {
    font-size: $font-size-sm;
    color: $color-text-muted;
    font-style: italic;
  }

  // Public profile settings
  &__public-toggle-row {
    margin-bottom: $space-4;
  }

  &__public-toggle-label {
    display: flex;
    align-items: center;
    gap: $space-3;
    cursor: pointer;
    font-size: $font-size-md;
  }

  &__public-link-row {
    display: flex;
    align-items: center;
    gap: $space-3;
    margin-top: $space-3;
  }

  &__public-link-url {
    font-size: $font-size-sm;
    color: $color-text-muted;
    word-break: break-all;
  }

  &__public-section-label {
    font-size: $font-size-sm;
    color: $color-text-muted;
    margin-bottom: $space-3;
  }

  &__public-field-list {
    display: flex;
    flex-direction: column;
    gap: $space-2;
  }

  &__public-field-item {
    display: flex;
    align-items: center;
    gap: $space-2;
    font-size: $font-size-md;
    cursor: pointer;
  }
}
</style>
